import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import apiService from '../services/nextlevelgames-api';

const MIN_CHARS = 2;
const PREVIEW_LIMIT = 6;
const DEBOUNCE_MS = 250;

const toProduct = (item) => ({
  name: item.name,
  description: item.description,
  description_html: item.description_html,
  price: item.price_money ? item.price_money.amount / 100 : 0,
  imageUrl: item.image_urls?.[0] || null,
  categories: item.categories || [],
  category: item.categories?.[0],
  updatedAt: item.updated_at || null,
  variation_id: item.variation_id,
  variation_name: item.variation_name,
  location_id: item.location_id,
  present_at_location_ids: item.present_at_location_ids || [],
  quantities_by_location: item.quantities_by_location || {},
  online_available: item.online_available === true,
  is_event: item.is_event === true,
});

// Search field with a live product preview that appears as the customer types.
// With `initialQuery` (the search page) the text stays in the field after a
// search and follows the page's query.
const SearchBox = ({ onNavigate, autoFocus = false, initialQuery = null, compact = false, placeholder = 'Search for products...' }) => {
  const keepQuery = initialQuery !== null;
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapperRef = useRef(null);

  const trimmed = query.trim();

  // Debounced fetch; the cleanup flag drops responses for stale queries.
  useEffect(() => {
    if (keepQuery) setQuery(initialQuery);
  }, [keepQuery, initialQuery]);

  useEffect(() => {
    if (!open || trimmed.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      apiService
        .searchCatalog(trimmed, PREVIEW_LIMIT, 0, null, null, false, false)
        .then((data) => {
          if (!cancelled) setResults((data.items || []).map(toProduct));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, open]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const finish = () => {
    setOpen(false);
    if (!keepQuery) {
      setQuery('');
      setResults([]);
    }
    if (onNavigate) onNavigate();
  };

  const goToResultsPage = () => {
    if (!trimmed) return;
    const q = trimmed;
    finish();
    if (keepQuery) setQuery(q);
    navigate(`/shop/search?q=${encodeURIComponent(q)}`);
  };

  const goToProduct = (product) => {
    finish();
    navigate(`/catalog/${encodeURIComponent(product.name)}`, { state: { item: product } });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (active >= 0 && results[active]) goToProduct(results[active]);
    else goToResultsPage();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown' && results.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp' && results.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    }
  };

  const showPanel = open && trimmed.length >= MIN_CHARS;

  return (
    <div ref={wrapperRef} className="relative">
      <form onSubmit={handleSubmit} className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full rounded-full border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
            compact ? 'pl-10 pr-4 py-2 text-sm' : 'px-12 py-3'
          }`}
          autoFocus={autoFocus}
          autoComplete="off"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="search-preview"
        />
        <Search className={`absolute top-1/2 transform -translate-y-1/2 text-gray-400 ${compact ? 'left-3.5 w-4 h-4' : 'left-4 w-5 h-5'}`} />
        {compact ? (
          <button type="submit" className="sr-only">Search</button>
        ) : (
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
          >
            Search
          </button>
        )}
      </form>

      {showPanel && (
        <div
          id="search-preview"
          className={`absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 ${compact ? 'right-0 w-[26rem] max-w-[90vw] max-lg:fixed max-lg:inset-x-3 max-lg:top-[68px] max-lg:w-auto max-lg:max-w-none' : 'left-0 right-0'}`}
        >
          <button
            type="button"
            onClick={goToResultsPage}
            className="w-full flex items-center justify-center gap-1 px-4 py-3 text-sm font-semibold text-primary-700 border-b border-gray-100 hover:bg-gray-50"
          >
            View all results
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
            Products
          </div>

          {loading && results.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">Searching…</p>
          )}
          {!loading && results.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">No products found for “{trimmed}”.</p>
          )}

          <ul className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            {results.map((product, i) => (
              <li key={product.variation_id || product.name}>
                <button
                  type="button"
                  onClick={() => goToProduct(product)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-4 px-4 py-2.5 text-left ${
                    i === active ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-12 h-14 flex-shrink-0 flex items-center justify-center">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt="" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <span className="text-2xl" aria-hidden="true">🎴</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 line-clamp-2">{product.name}</div>
                    {product.variation_name && (
                      <div className="text-xs text-gray-500 line-clamp-1">{product.variation_name}</div>
                    )}
                    <div className="text-sm font-semibold text-primary-700">${product.price.toFixed(2)}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>

        </div>
      )}
    </div>
  );
};

export default SearchBox;
